<template>
  <div class="h-full flex flex-col bg-gray-50">
    <!-- Settings Header -->
    <div class="bg-white border-b border-gray-200 px-8 py-6">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-bold text-gray-900">Firmeneinstellungen</h1>
          <p class="text-sm text-gray-500 mt-1">Verwalten Sie Ihre Firmeninformationen und Mitglieder</p>
        </div>
        <UButton
          label="Zurück zu Projekten"
          icon="i-lucide-arrow-left"
          color="gray"
          variant="outline"
          @click="$emit('close')"
        />
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex-1 overflow-y-auto p-8">
      <div class="max-w-4xl mx-auto space-y-8">
        
        <!-- Company Information -->
        <div class="bg-white rounded-lg border border-gray-200 p-6">
          <h2 class="text-lg font-semibold text-gray-900 mb-4">Firmeninformationen</h2>
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Firmenname</label>
              <UInput v-model="companyName" placeholder="Firmenname eingeben" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Beschreibung</label>
              <UTextarea 
                v-model="companyDescription" 
                placeholder="Firmenbeschreibung eingeben..."
                :rows="4"
                autoresize
              />
            </div>
            <div class="flex justify-end">
              <UButton
                label="Änderungen speichern"
                color="primary"
                @click="saveCompanyInfo"
                :disabled="!hasChanges"
              />
            </div>
          </div>
        </div>

        <!-- Members List -->
        <div class="bg-white rounded-lg border border-gray-200 p-6">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-lg font-semibold text-gray-900">Mitglieder</h2>
            <UButton
              label="Mitglied einladen"
              icon="i-lucide-user-plus"
              color="primary"
              size="sm"
              @click="showInviteModal = true"
            />
          </div>
          
          <div class="space-y-3">
            <div v-for="member in company?.members || []" :key="member.id" 
                 class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div class="flex items-center gap-3">
                <div class="size-10 rounded-full bg-gray-300 flex items-center justify-center">
                  <UIcon name="i-lucide-user" class="size-5 text-gray-700" />
                </div>
                <div>
                  <p class="font-medium text-gray-900">{{ member.fullName }}</p>
                  <p class="text-sm text-gray-500">{{ member.email }}</p>
                </div>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-sm px-2 py-1 rounded-full"
                      :class="member.privilege === 'Admin' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'">
                  {{ member.privilege }}
                </span>
                <UDropdown v-if="member.id !== currentUser?.id">
                  <UButton
                    icon="i-lucide-more-vertical"
                    color="gray"
                    variant="ghost"
                    size="sm"
                  />
                  <template #content>
                    <UDropdownItem label="Rolle ändern" icon="i-lucide-shield" />
                    <UDropdownItem label="Entfernen" icon="i-lucide-user-x" color="red" />
                  </template>
                </UDropdown>
              </div>
            </div>
          </div>
        </div>

        <!-- Danger Zone -->
        <div class="bg-white rounded-lg border border-red-200 p-6">
          <h2 class="text-lg font-semibold text-red-900 mb-4">Gefahrenzone</h2>
          <p class="text-sm text-gray-600 mb-4">
            Vorsicht: Diese Aktionen können nicht rückgängig gemacht werden.
          </p>
          <UButton
            label="Firma löschen"
            color="red"
            variant="outline"
            icon="i-lucide-trash-2"
            @click="showDeleteModal = true"
          />
        </div>

      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useCompanyStore } from '~/stores/company'
import { useAuthStore } from '~/stores/auth'

defineEmits(['close'])

const companyStore = useCompanyStore()
const authStore = useAuthStore()

const { company } = storeToRefs(companyStore)
const { currentUser } = storeToRefs(authStore)

const companyName = ref('')
const originalCompanyName = ref('')
const companyDescription = ref('')
const originalCompanyDescription = ref('')

const showInviteModal = ref(false)
const showDeleteModal = ref(false)

// Initialize reactive data when company loads
watch(company, (newCompany) => {
  if (newCompany) {
    companyName.value = newCompany.name
    originalCompanyName.value = newCompany.name
    companyDescription.value = newCompany.description
    originalCompanyDescription.value = newCompany.description
  }
}, { immediate: true })

const hasChanges = computed(() => {
  return companyName.value !== originalCompanyName.value || 
         companyDescription.value !== originalCompanyDescription.value
})

const saveCompanyInfo = () => {
  if (company.value) {
    companyStore.updateCompany({
      name: companyName.value,
      description: companyDescription.value
    })
    originalCompanyName.value = companyName.value
    originalCompanyDescription.value = companyDescription.value
  }
}
</script>